import { Location } from '@angular/common';
import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { CanDeactivateComponent } from '@app/interfaces';
import { ApplicantIndividualTestMessage } from '@app/modules/applicant-tracker/interfaces/bulk-applicant-message';
import { ApplicantMessageService } from '@app/modules/applicant-tracker/services';
import { FunnelMessageTemplateForm } from '@app/modules/forms/applicant-tracker/components';
import { AnalyticService, NotifyService } from '@app/services';
import { FunnelMessageTemplate } from '@models/recruiting/funnel-message-template.model';
import { FunnelTemplateStage } from '@models/recruiting/funnel-template-stages.model';

@Component({
    templateUrl: './funnel-message-template-store.template.html',
})
export class FunnelMessageTemplateStoreView implements CanDeactivateComponent {
    isLoading = false;
    funnelTemplateStage: FunnelTemplateStage;
    isSaveModalOpen = false;
    isDeactivateModalOpen = false;
    canNavigateBack = true;
    previousPath: string;

    funnelMessageTemplate: FunnelMessageTemplate;

    constructor(
        private route: ActivatedRoute,
        private applicantMessageService: ApplicantMessageService,
        private notifyService: NotifyService,
        private analyticService: AnalyticService,
        private location: Location,
        private router: Router
    ) {
        this.funnelTemplateStage = this.route.snapshot.data.models.get('funnelTemplateStage');
        this.funnelMessageTemplate = new FunnelMessageTemplate({
            atsFunnelTemplateStageId: this.funnelTemplateStage.id,
        });
    }

    canDeactivate(): boolean {
        if (this.funnelMessageTemplate.body) {
            this.canNavigateBack = false;
            this.isDeactivateModalOpen = true;
            this.previousPath = this.previousPath ?? this.location.path();
        }

        return this.canNavigateBack;
    }

    allowNavigation(): void {
        this.canNavigateBack = true;
        this.funnelMessageTemplate.body = '';
        this.router.navigateByUrl(this.previousPath);
    }

    openSave(): void {
        this.isSaveModalOpen = true;
    }

    closeSave(): void {
        this.isSaveModalOpen = false;
    }

    closeDeactivateModalOpen(): void {
        this.isDeactivateModalOpen = false;
    }

    async onSave(funnelMessageTemplateForm: FunnelMessageTemplateForm): Promise<void> {
        funnelMessageTemplateForm.submit();

        if (!funnelMessageTemplateForm.valid) {
            this.closeSave();
            return;
        }

        this.isLoading = true;

        try {
            await this.funnelMessageTemplate.save();

            this.notifyService.success({
                key: 'applicant-tracker.subjectWillBeAutomaticallySentToApplicantsInFunnelName',
                params: {
                    subject: this.funnelMessageTemplate.subject ?? '',
                    funnelName: this.funnelTemplateStage.name,
                },
            });

            this.analyticService.trackEvent(
                AnalyticEvents.RecruitingFunnelTemplatesMeatballEditMeatballAddEmailAutomationSave
            );

            this.funnelMessageTemplate.body = '';
            this.canNavigateBack = true;
            this.previousPath ? this.router.navigateByUrl(this.previousPath) : this.location.back();
        } catch (err) {
            this.notifyService.error('applicant-tracker.failedToSaveATemplate');
        }

        this.isLoading = false;
        this.closeSave();
    }

    async onSendTest(funnelMessageTemplateForm: FunnelMessageTemplateForm): Promise<void> {
        funnelMessageTemplateForm.submit();

        if (!funnelMessageTemplateForm.valid) {
            return;
        }

        const funnelMessageTemplateTestMessage: ApplicantIndividualTestMessage = {
            body: this.funnelMessageTemplate.body,
            replyTo: this.funnelMessageTemplate.replyTo,
            subject: this.funnelMessageTemplate.subject,
        };

        this.isLoading = true;

        try {
            await this.applicantMessageService.onApplicantIndividualTestMessage(funnelMessageTemplateTestMessage);
            this.notifyService.success({
                key: 'applicant-tracker.testEmailSentToReplyTo',
                params: {
                    replyTo: this.funnelMessageTemplate.replyTo,
                },
            });

            this.analyticService.trackEvent(AnalyticEvents.RecruitingAllEmailsSendTest);
        } catch (err) {
            this.notifyService.error({
                key: 'applicant-tracker.testEmailFailedToSendToReplyTo',
                params: {
                    replyTo: this.funnelMessageTemplate.replyTo,
                },
            });
        } finally {
            this.isLoading = false;
        }
    }
}
